import { logger } from '@adornis/base/logging.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import {
  _cancelEnrollment,
  moveUpFromWaitinglistIntoParticipating,
  setParticipatedByID,
} from '@adornis/buchungsheld-db/api/participation.js';
import { bhBaseQlContext } from '@adornis/buchungsheld-db/bh-context.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { notifyAbmeldungDurchEinrichtung } from '@adornis/buchungsheld-db/notification/abmeldung-durch-einrichtung.js';
import { notifyBestaetigungWorkshopRegistrierung } from '@adornis/buchungsheld-db/notification/bestaetigung-workshop-registrierung.js';
import { sendEinzelberatungMeetingInfoMailsIfAlreadySent } from '@adornis/buchungsheld-db/notification/einzelberatung-meeting-infos.js';
import { sendMeetingInfoMailsIfAlreadySent } from '@adornis/buchungsheld-db/notification/meeting-infos.js';
import { notifyNichtErschienen } from '@adornis/buchungsheld-db/notification/nicht-erschienen.js';
import { notifyNuzterSperrungAufgehoben } from '@adornis/buchungsheld-db/notification/nutzer-sperrung-aufgehoben.js';
import { notifyNuzterSperrung } from '@adornis/buchungsheld-db/notification/nutzer-sperrung.js';
import { notifyVeranstaltungseintragungDurchAdmin } from '@adornis/buchungsheld-db/notification/veranstaltungseintragung-durch-admin.js';
import { forceNachrueckLogic } from './waitinglist-monitoring.js';

export async function _updateParticipationStateAsAdminUser(
  participations: NarrowedEntityData<
    Participation,
    {
      _id: 1;
      _class: 1;
      state: 1;
      userID: 1;
      productID: 1;
      updateParticipationsStateAsAdminUser: 1;
      lastStateChangeWasInitiatedByAdmin: 1;
      timeslotID: 1;
      waitingListPlace: 1;
      nachrueckPriority: 1;
      product: {
        _id: 1;
        _class: 1;
        institutionID: 1;
        settings: { blockSignOffDaysInAdvance: 1; inheritedBlockSignOffDaysInAdvance: 1 };
      };
    }
  >[],
  nextState: ParticipationState,
) {
  if (participations.length === 0) return 0;

  const productID = participations[0]!.productID;
  if (!productID) throw new Error('productID is not defined');

  for (const participation of participations) {
    if (participation.productID !== productID)
      throw new Error(
        'all participations must be from the same product. Got ' + productID + ' and ' + participation.productID,
      );
  }

  let doNachrueckLogicAtTheEnd = false;
  await Promise.all(
    participations.map(async _participation => {
      const currentState = _participation.state;
      if (nextState === currentState) return;

      const institutionID = _participation.product!.institutionID;
      if (!institutionID) {
        logger.error({ _participation }, 'Participation has no institutionID');
        throw new Error('Participation has no institutionID');
      }

      const user = await loadRawEntityDataByIDOrFail(User, _participation.userID, {
        _id: 1,
        email: 1,
        title: 1,
        username: 1,
        firstName: 1,
        lastName: 1,
        whatsappNotification: 1,
        suspentions: { _id: 1, institutionID: 1 },
        whatsappPhone: 1,
      });

      const participation =
        _participation instanceof Participation ? { ..._participation.toJSON(), user } : { ..._participation, user };

      const match = (inp1, inp2) => {
        const inp1Matches = inp1 === currentState || (Array.isArray(inp1) && inp1.includes(currentState));
        const inp2Matches = inp2 === nextState || (Array.isArray(inp2) && inp2.includes(nextState));
        return inp1Matches && inp2Matches;
      };

      switch (true) {
        // participated
        case match(ParticipationState.left, ParticipationState.participated):
        case match(ParticipationState.in, ParticipationState.participated):
        case match(ParticipationState.suspended, ParticipationState.participated):
        case match(ParticipationState.no_show, ParticipationState.participated):
          await setParticipatedByID(participation._id, true);
          break;

        case match(ParticipationState.waiting, ParticipationState.participated):
          await setParticipatedByID(participation._id, true);
          doNachrueckLogicAtTheEnd = true;
          break;

        // not participated
        case match(ParticipationState.participated, ParticipationState.in):
          await setParticipatedByID(participation._id, false);
          break;

        // waitinglist nachruecken
        case match(ParticipationState.waiting, ParticipationState.in):
          bhBaseQlContext.ignoreCapacity = true;
          logger.info({ participation, id: participation._id }, 'waitinglist nachruecken');
          await moveUpFromWaitinglistIntoParticipating(participation._id, null)({});
          doNachrueckLogicAtTheEnd = true;
          break;

        // reparticipate
        case match(ParticipationState.left, ParticipationState.in): {
          //! overlapping time check gets ignored right now
          // update state in DB before notification so it reads the correct state
          const col = mongo().collection<Participation>(Participation._collectionName);
          await col.updateOne({ _id: participation._id }, { $set: { state: nextState, updatedAt: new Date() } });
          await notifyVeranstaltungseintragungDurchAdmin(participation._id);
          break;
        }

        // back to warteliste
        case match(ParticipationState.left, ParticipationState.waiting): {
          //! overlapping time check gets ignored right now
          const withCorrectState = { ...participation, state: nextState, waitingListPlace: null };
          await notifyBestaetigungWorkshopRegistrierung(institutionID, user as User, [withCorrectState]);
          doNachrueckLogicAtTheEnd = true;
          break;
        }

        // abgemeldet
        case match(ParticipationState.participated, ParticipationState.left):
        case match(ParticipationState.in, ParticipationState.left):
          await _cancelEnrollment(participation, true);
          doNachrueckLogicAtTheEnd = true;
          break;

        // waitinglist
        case match(ParticipationState.participated, ParticipationState.waiting):
        case match(ParticipationState.in, ParticipationState.waiting): {
          const withCorrectState = { ...participation, state: nextState, waitingListPlace: null };
          await notifyBestaetigungWorkshopRegistrierung(institutionID, user as User, [withCorrectState]);
          doNachrueckLogicAtTheEnd = true;
          break;
        }

        // leaving the waitinglist
        case match([ParticipationState.waiting], ParticipationState.left):
          await notifyAbmeldungDurchEinrichtung({ participationID: participation._id });
          doNachrueckLogicAtTheEnd = true;
          break;

        // ingnore these abmeldungen
        case match([ParticipationState.suspended], ParticipationState.left):
        case match(ParticipationState.no_show, ParticipationState.left):
          break;

        // suspension
        case match(ParticipationState.participated, ParticipationState.suspended):
        case match(ParticipationState.in, ParticipationState.suspended):
          await notifyNuzterSperrung([participation._id]);
          doNachrueckLogicAtTheEnd = true;
          break;

        case match(ParticipationState.waiting, ParticipationState.suspended):
          await notifyNuzterSperrung([participation._id]);
          doNachrueckLogicAtTheEnd = true;
          break;

        // suspension cancled
        case match(ParticipationState.suspended, ParticipationState.in):
        case match(ParticipationState.suspended, ParticipationState.left):
          await notifyNuzterSperrungAufgehoben([participation._id]);
          break;

        case match(ParticipationState.suspended, ParticipationState.waiting):
          await notifyNuzterSperrungAufgehoben([participation._id]);
          doNachrueckLogicAtTheEnd = true;
          break;

        // ignore these suspensions
        case match(ParticipationState.left, ParticipationState.suspended):
          break;

        // ingnore
        case match(ParticipationState.in, ParticipationState.in):
        case match(ParticipationState.left, ParticipationState.left):
          break;

        // no shows can currently only be moved back to in and nothing will happen
        case match(ParticipationState.no_show, ParticipationState.in):
          break;

        case match(Object.values(ParticipationState), ParticipationState.no_show):
          await notifyNichtErschienen(participation._id);
          break;

        default:
          throw new Error(
            'state transition not implemented in side effect matrix: ' + currentState + ' -> ' + nextState,
          );
      }
    }),
  );

  bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = true;
  const collection = mongo().collection<Participation>(Participation._collectionName);
  await collection.updateMany(
    {
      _id: { $in: participations.map(p => p._id) },
    },
    {
      $set: {
        state: nextState,
        lastStateChangeWasInitiatedByAdmin: true,
        updatedAt: new Date(),
        ...(nextState !== ParticipationState.waiting ? { waitingListPlace: null } : {}),
      },
    },
  );

  // send meeting info mails to participants who were just moved to 'in' state
  if (nextState === ParticipationState.in) {
    const productClass = participations.at(0)?.product?._class;
    for (const p of participations) {
      // skip if already in 'in' state (no actual change) or 'waiting' (handled by moveUpFromWaitinglistIntoParticipating)
      if (p.state === ParticipationState.in || p.state === ParticipationState.waiting) continue;
      if (productClass === 'Workshop') {
        sendMeetingInfoMailsIfAlreadySent(p._id, productID);
      } else if (productClass === 'Einzelberatung') {
        // timeslotID lives on ParticipationEinzelberatung; NarrowedEntityData on the base
        // Participation type strips it even though the selection set requests it.
        const timeslotID = (p as { timeslotID?: string | null }).timeslotID;
        sendEinzelberatungMeetingInfoMailsIfAlreadySent(p._id, productID, timeslotID);
      }
    }
  }

  // NOTE: When admins change participation states, we force immediate nachrueck logic execution
  // This ensures admins get immediate feedback about waitinglist changes
  if (participations.at(0)?.product?._class === 'Workshop' && doNachrueckLogicAtTheEnd) {
    try {
      await forceNachrueckLogic(productID);
    } catch (err) {
      logger.error({ err, productID }, 'Error forcing nachrueck logic after admin state change');
    }
  }

  return 1;
}
